/**
 * utils/contactImportApplyGroups.js
 * Contacts xlsx import, batch 5c (the LAST piece): turns a human's CONFIRMED subset of
 * `contactImportGroup.js`'s own `groupProposedFirms` proposal into real `Business` records,
 * linked to the real `Contact`s `contactImportCommit.js` just created. No React, no UI — the
 * wizard (`pages/ImportKontakWizard.js`) is what shows the proposal, collects a human's per-group
 * decision, and calls this. Pure, deterministic, never mutates any input.
 *
 * ── WHERE THIS SITS IN THE CHAIN ────────────────────────────────────────────────────────────
 *   resolveContactImport -> resolveSalesPersonMapping -> commitContactImport -> applyConfirmedGroups -> ONE update()
 * `groupProposedFirms` itself runs EARLIER and separately — it's the wizard's own step-6 review
 * screen, computed once (it costs ~5s on the real file) off `resolveSalesPersonMapping`'s own
 * final `plan`, the same plan `commitContactImport` commits moments later. This module never
 * re-runs `groupProposedFirms` — it takes that already-computed `groups` array (or whatever
 * subset the wizard still has around) and a human's decisions about it, applied against `data`
 * AFTER `commitContactImport` has already run, since a group's own members must already BE real
 * `Contact`s before any of them can carry a `businessId`.
 *
 * ── TWO CORRECTIONS RECORDED BEFORE THIS WAS BUILT (`src/pages/CLAUDE.md`'s own batch-5a-era
 *    review note) — both are load-bearing design decisions here, not incidental choices ────────
 *
 * 1. NEVER READ A NEWLY-MINTED BUSINESS BACK BY LIST POSITION, UNGUARDED. `addBusiness`
 *    (`businesses.js`) is a same-reference no-op on a blank name — it does NOT always append.
 *    `App.js`'s own `handleLinkContacts` reads `nd.businesses[nd.businesses.length-1]` right
 *    after its own `addBusiness` call, and that IS safe there, but only because it already
 *    refused a blank name several lines earlier — never because position is generally safe.
 *    This module refuses to even ATTEMPT `addBusiness` for a group without a real, non-blank
 *    typed name (the wizard's own "Confirm" control is expected to be structurally unable to
 *    reach this function with one, but this is checked here again, in the pure layer, as the
 *    real gate — defense in depth, the same discipline `contactImportCommit.js` already uses for
 *    `addContact`'s own "cannot rationally refuse" branch). Only once that's confirmed non-blank
 *    does this module call `addBusiness` and check `nd === data` (the exact same reference-
 *    equality refusal detection `contactImportSalesMap.js` already uses for `addPerson`) BEFORE
 *    reading anything back — a refusal here is reported, never silently treated as success, and
 *    `nd.businesses[nd.businesses.length-1]` is read ONLY in the branch where both guards already
 *    passed, one group at a time (never two `addBusiness` calls without reading the result
 *    between them), so there is no window where "the last business" could be some OTHER group's.
 *
 * 2. THE IMPORT MUST NEVER MODIFY A CONTACT IT DID NOT CREATE IN THIS RUN. A group's own member
 *    is a resolved PLAN ROW, not yet a real Contact — `commitContactImport` either CREATED a new
 *    Contact for it, SKIPPED it (a contact with that name already existed — create-only, never-
 *    replace), or FAILED it (a validation problem). Resolving a member back to a real Contact by
 *    `(list, name)` after commit cannot, on its own, tell these apart — a skipped row's name
 *    already resolves to a real Contact too, just one the user already had, possibly already
 *    linked to a different business of their own choosing. Silently linking it would overwrite
 *    that businessId (or fold a stranger's existing contact into a new business alongside shops
 *    it has nothing to do with) — breaking create-only/never-replace from the other direction.
 *    **This module ONLY EVER calls `updateContact` on a member whose own row is provably absent
 *    from `commitReport.skipped` AND `commitReport.failed`** (matched by `(list, row)` — see
 *    `memberOutcome` below, never a bare post-commit `(list, name)` lookup on its own). A skipped
 *    member is reported with the caller's own required message, `"sudah ada, tidak ditautkan
 *    otomatis"`, and its Contact record is never touched — not even read for comparison.
 *
 * ── WHY `(list, row)`, NOT `(list, name)`, IS THE MEMBER<->REPORT-ENTRY KEY ─────────────────
 * `commitContactImport`'s own report entries (`skipped`/`failed`) carry `{list, name, row,
 * sourceRows}` — `row` is the shop's own ANCHOR row (`contactImportSalesMap.js`'s own "restores
 * row/sourceRows at the top level from _meta" fix; every plan row reaching this module already
 * carries it). `row` is a genuine, unique-per-sheet identifier for a shop's own EFFECTIVE
 * identity (one real source row per shop, even after duplicate-name merging) — `name` is not
 * always unique across the whole plan (two rows batch 3a's own duplicate-name step left
 * deliberately SEPARATE, still sharing a name because the user never renamed either — see
 * `contactImportResolve.js`'s own "a rename colliding with a name OUTSIDE its own group is not
 * checked here" note). `(list, row)` never has this ambiguity.
 * **The post-commit CONTACT lookup itself (for a member already proven CREATED) is safely done
 * by `(list, name)`** — this is NOT the same risk: `validateContactFields`'s own name-collision
 * rule guarantees at most one contact per `(list, name)` at rest, and a row classified as
 * CREATED (not skipped/failed) means no pre-existing contact had that name when it committed and
 * `commitContactImport`'s own accumulating collision check guarantees no OTHER plan row in this
 * same run created a second contact under the identical `(list, name)` either (the second such
 * row would itself have been SKIPPED as colliding with the first) — so exactly one match always
 * exists for a genuinely-created member, and it is unambiguous.
 *
 * ── A CONFIRMED GROUP WHOSE TYPED NAME MATCHES AN EXISTING BUSINESS ─────────────────────────
 * Legal, unremarkable, nothing special happens: `businesses.js`'s own header states two real
 * firms may share a display name on purpose (a Pusat entry's business name is the legal entity,
 * a Cabang entry's is the shop name — they can differ for the SAME business too), and
 * `addBusiness` has no uniqueness check at all, matching `BusinessLinkModal`'s own existing
 * "name step never checks for a collision" behavior. A second, DISTINCT `Business` record is
 * created, same as it would be anywhere else in this app.
 *
 * ── A GROUP WHOSE MEMBERS MOSTLY FAILED TO BE CREATED ───────────────────────────────────────
 * Never silently shrunk into a business of one. If fewer than TWO of a confirmed group's
 * effective members were actually created, NO `Business` is minted for that group at all — every
 * one of its effective members (created or not) is reported in `notLinked`, each with its own
 * real reason, so nothing is hidden; the group itself never appears in `createdBusinesses`.
 *
 * ── DECISIONS SHAPE ─────────────────────────────────────────────────────────────────────────
 * `decisions` is a flat map keyed by a group's own INDEX in the `groups` array handed in —
 * deliberately positional, since an emergent computed cluster (unlike a single shop, which has a
 * real, stable `shopKey`) has no natural identity of its own across two different runs of
 * `groupProposedFirms`. **This is only ever safe for the SAME `groups` array the wizard already
 * has in hand** — the wizard's own job is to throw `decisions` away (reset to `{}`) the instant
 * it recomputes `groupProposedFirms` for any reason (an earlier step changed), exactly the
 * invalidation this module's own caller-side contract requires; this module itself has no way to
 * detect a stale pairing and does not try to.
 *   decisions[groupIndex] = {
 *     status: "confirmed" | "rejected",   // absent/undecided = leave alone, create nothing
 *     name?: string,                      // required (and only read) when status === "confirmed"
 *     removedKeys?: string[],             // shopKey(list,row) values the human removed from
 *                                          // this group — see contactImportResolve.js's own
 *                                          // exported `shopKey`, reused verbatim, never re-derived
 *   }
 * A group with fewer than two EFFECTIVE members (after `removedKeys` is applied) has nothing to
 * confirm — the wizard's own "Confirm" control is expected to already disable itself there; this
 * module treats such a group exactly like "insufficient members were created" above (reports
 * every remaining effective member in `notLinked`, mints nothing) as its own defense in depth.
 *
 * ── RETURN SHAPE ────────────────────────────────────────────────────────────────────────────
 * @returns {{
 *   data: object,
 *   createdBusinesses: { groupIndex:number, businessId:string, name:string,
 *     members: {list:string, name:string}[] }[],
 *   notLinked: { groupIndex:number, list:string, name:string, reason:string }[],
 *   undecidedGroups: { groupIndex:number, memberNames:string[] }[],
 * }}
 * `notLinked` covers ONLY members of CONFIRMED groups — a REJECTED group's members are never
 * listed here (rejection is itself the human's own stated, already-visible decision; there is
 * nothing further to explain), and an UNDECIDED group's members are named instead in
 * `undecidedGroups`, never duplicated into `notLinked` too. Every effective member of every
 * confirmed group lands in exactly one of `createdBusinesses[].members` or `notLinked` — nothing
 * silently dropped, matching this wizard's own "nothing that will be left out may be hidden"
 * rule stated everywhere else in this import.
 */
import { addBusiness } from "./businesses";
import { updateContact } from "./contacts";
import { shopKey } from "./contactImportResolve";

function norm(s) {
  return String(s == null ? "" : s).trim();
}

/** Whether `member`'s own row was SKIPPED or FAILED by `commitContactImport` — matched by
 *  `(list, row)`, never `(list, name)` (see this file's own header for why). `row` is read the
 *  same way `contactImportCommit.js`'s own `traceFields` reads it, off the top-level `row` every
 *  plan row carries by the time it reaches this module (restored by `contactImportSalesMap.js`,
 *  never `_meta.anchorRow` directly — that restoration is exactly what makes `row` reliable here
 *  without this module needing to know about `_meta` at all). */
function memberOutcome(shop, commitReport) {
  const list = shop && shop.list;
  const row = shop ? shop.row : null;
  const skip = (commitReport.skipped || []).find((s) => s.list === list && s.row === row);
  if (skip) return { status: "skipped" };
  const fail = (commitReport.failed || []).find((f) => f.list === list && f.row === row);
  if (fail) return { status: "failed", message: fail.message };
  return { status: "created" };
}

/** The one real Contact a CREATED member resolves to — `(list, name)`, safe ONLY for a member
 *  `memberOutcome` already classified as "created" (see this file's own header for why). */
function findCreatedContact(contacts, list, name) {
  const key = norm(name).toLowerCase();
  return (contacts || []).find((c) => c.list === list && norm(c.name).toLowerCase() === key) || null;
}

function normalizeGroupDecision(raw) {
  const isObj = raw != null && typeof raw === "object";
  const status = isObj && (raw.status === "confirmed" || raw.status === "rejected") ? raw.status : null;
  const name = isObj && typeof raw.name === "string" ? raw.name : "";
  const removedKeys = isObj && Array.isArray(raw.removedKeys) ? raw.removedKeys : [];
  return { status, name, removedKeys };
}

/**
 * Applies a human's CONFIRMED subset of `groupProposedFirms`'s own proposal to `data` (already
 * post-`commitContactImport`). Pure — returns a new `{data, createdBusinesses, notLinked,
 * undecidedGroups}`; never mutates `groups`, `decisions`, `commitReport`, or the input `data`.
 * @param {object} data — `commitContactImport`'s own returned `data` (real `.contacts`/`.businesses`).
 * @param {object[]} groups — `groupProposedFirms(...).groups` — see that module's own return shape.
 * @param {object} decisions — see this file's own header for the exact per-group shape.
 * @param {object} commitReport — `commitContactImport`'s own returned `report` (for `skipped`/`failed`).
 */
export function applyConfirmedGroups(data, groups, decisions, commitReport) {
  const groupList = Array.isArray(groups) ? groups : [];
  const dec = decisions || {};
  const report = commitReport || { skipped: [], failed: [] };

  let nd = data;
  const createdBusinesses = [];
  const notLinked = [];
  const undecidedGroups = [];

  groupList.forEach((group, groupIndex) => {
    const { status, name, removedKeys } = normalizeGroupDecision(dec[groupIndex]);

    if (!status) {
      undecidedGroups.push({
        groupIndex,
        memberNames: group.members.map((m) => m.shop.name),
      });
      return;
    }
    if (status === "rejected") return; // a stated decision — nothing created, nothing to explain

    const removedSet = new Set(removedKeys);
    const effective = group.members.filter((m) => !removedSet.has(shopKey(m.shop.list, m.shop.row)));

    const outcomes = effective.map((m) => ({ member: m, outcome: memberOutcome(m.shop, report) }));
    const created = outcomes.filter((o) => o.outcome.status === "created");

    const trimmedName = norm(name);
    const canForm = created.length >= 2 && trimmedName.length > 0;

    if (!canForm) {
      // Insufficient created members, OR a blank name reached this defense-in-depth gate — either
      // way, never silently shrink this into a business of one, or one with no real name. Every
      // effective member is reported, each with its own real reason.
      for (const { member, outcome } of outcomes) {
        const reason =
          outcome.status === "skipped" ? "sudah ada, tidak ditautkan otomatis"
          : outcome.status === "failed" ? outcome.message
          : !trimmedName ? "kelompok tidak dikonfirmasi — nama bisnis kosong"
          : "kelompok tidak dibuat — kurang dari dua toko berhasil dibuat di kelompok ini";
        notLinked.push({ groupIndex, list: member.shop.list, name: member.shop.name, reason });
      }
      return;
    }

    // Not-created members of an otherwise-forming group are still reported individually.
    for (const { member, outcome } of outcomes) {
      if (outcome.status === "created") continue;
      notLinked.push({
        groupIndex, list: member.shop.list, name: member.shop.name,
        reason: outcome.status === "skipped" ? "sudah ada, tidak ditautkan otomatis" : outcome.message,
      });
    }

    const nd2 = addBusiness(nd, { name: trimmedName });
    if (nd2 === nd) {
      // Refused (blank name) — unreachable given the `canForm` guard above; defense in depth only.
      for (const { member } of created) {
        notLinked.push({ groupIndex, list: member.shop.list, name: member.shop.name, reason: "gagal membuat bisnis (nama kosong)" });
      }
      return;
    }
    nd = nd2;
    // Safe here specifically: a real, non-blank name was already confirmed non-blank above, `nd2`
    // just proved `addBusiness` actually appended, and no OTHER `addBusiness` call runs between
    // this one and this read (one group fully handled at a time) — see this file's own header.
    const business = nd.businesses[nd.businesses.length - 1];

    const linkedMembers = [];
    for (const { member } of created) {
      const contact = findCreatedContact(nd.contacts, member.shop.list, member.shop.name);
      if (!contact) {
        // Unreachable given `memberOutcome`'s own guarantee for a "created" row — defense in depth.
        notLinked.push({ groupIndex, list: member.shop.list, name: member.shop.name, reason: "kontak tidak ditemukan setelah dibuat" });
        continue;
      }
      const nd3 = updateContact(nd, contact.id, { businessId: business.id });
      if (nd3 === nd) {
        notLinked.push({ groupIndex, list: member.shop.list, name: member.shop.name, reason: "gagal menautkan ke bisnis (sebab tidak diketahui)" });
        continue;
      }
      nd = nd3;
      linkedMembers.push({ list: member.shop.list, name: member.shop.name });
    }

    // The "at least two" gate already ran above, against CREATED members (`canForm`) — that is
    // the real, documented threshold ("fewer than two members remain" in the task's own words).
    // `linkedMembers` can only fall short of `created.length` through the defense-in-depth
    // branches just above (unreachable given this module's own guarantees), each of which already
    // reports its own specific member via `notLinked` — a member that DID succeed is reported here,
    // as linked, regardless of whether an unrelated sibling member hit one of those dead branches.
    createdBusinesses.push({ groupIndex, businessId: business.id, name: business.name, members: linkedMembers });
  });

  return { data: nd, createdBusinesses, notLinked, undecidedGroups };
}
